// Full-farm feed table (P1, findings #101): one row per unit with what the
// office needs to set tomorrow's ration — fish, average weight, biomass,
// temperature, SFR, recommended kg/day (SFR × biomass), the unit's plan kg/day,
// what was fed yesterday, density and flow per kg. Pure: the component loads
// the inputs. ASC 4.1.1.9 (feeding plan); GLOBALG.A.P. AQ-GFS 20.02.13 (density).

import {
  densityLimitFor,
  flowPerKg,
  stockingDensityKgM3,
  unitVolumeM3,
  type DensityLimitBand
} from '../../utils/density'
import { capacityDensityLimit, capacityMinFlowPerKg, type CarryingCapacityTable } from '../../utils/carryingCapacity'

export type FarmFeedLotInput = {
  id: string
  lotKey: string
  /** Eggs / yolk-sac: no feeding and no kg/m³ yet (Hatchery v0 rule). */
  preFeeding: boolean
  /** Findings #160: the lot's lineage root — "Sembrar en varias unidades" makes one lot per unit sharing it. */
  lineageRootId?: string | null
  fishCount: number | null
  averageWeightG: number | null
  biomassKg: number | null
  /** Day of the record the weight / stock come from (findings #113). */
  asOfDate?: string | null
}

export type FarmFeedUnitInput = {
  id: string
  name: string
  type?: string
  capacity?: unknown
  capacityUnit?: unknown
  lots: FarmFeedLotInput[]
  temperatureC: number | null
  /** Day of the temperature reading (findings #113). */
  temperatureDate?: string | null
  flowLpm: number | null
  planKgPerDay: number | null
  /** Findings #130: feedings per day of the unit's plan. */
  planFeedingsPerDay?: number | null
  fedYesterdayKg: number | null
  /** Findings #135: fed so far today (farm day). */
  fedTodayKg?: number | null
}

export type FarmFeedRow = {
  unitId: string
  unitName: string
  lots: Array<{ id: string; lotKey: string; preFeeding: boolean; lineageRootId?: string | null }>
  fishCount: number | null
  /** Eggs / yolk-sac alevins in the unit (their opening stock, findings #110). */
  eggCount: number | null
  averageWeightG: number | null
  biomassKg: number | null
  temperatureC: number | null
  /** Oldest weight day among the feeding lots, and the temperature day (YYYY-MM-DD), findings #113. */
  weightDate: string | null
  temperatureDate: string | null
  /** Biomass-weighted SFR of the feeding lots, %/day. */
  sfrPercent: number | null
  recommendedKgPerDay: number | null
  planKgPerDay: number | null
  planFeedingsPerDay: number | null
  fedYesterdayKg: number | null
  fedTodayKg: number | null
  densityKgM3: number | null
  densityLimitKgM3: number | null
  /** The Capacidad de carga table of the unit's feeding lot (findings #151); null = the #96 facility limits / none. */
  densityTableName: string | null
  flowLpmPerKg: number | null
  /** Minimum L/min per kg at this weight and the unit's temperature (findings #151). */
  minFlowLpmPerKg: number | null
}

const sumOrNull = (values: Array<number | null>): number | null => {
  const present = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
  return present.length > 0 ? present.reduce((total, value) => total + value, 0) : null
}

export const buildFarmFeedRows = (
  units: FarmFeedUnitInput[],
  options: {
    /** SFR %/day for a lot at this weight and temperature, or null. */
    sfrFor: (lotId: string, averageWeightG: number, temperatureC: number) => number | null
    /** #96 facility limits, used when no Capacidad de carga table applies. */
    densityLimits?: DensityLimitBand[]
    /** Findings #151: the lot's Capacidad de carga table (its copy, else the facility default). */
    capacityForLot?: (lotId: string) => CarryingCapacityTable | null
  }
): FarmFeedRow[] => units.map((unit) => {
  const feedingLots = unit.lots.filter((lot) => !lot.preFeeding)
  const fishCount = sumOrNull(feedingLots.map((lot) => lot.fishCount))
  const biomassKg = sumOrNull(feedingLots.map((lot) => lot.biomassKg))
  const averageWeightG = biomassKg !== null && fishCount !== null && fishCount > 0
    ? (biomassKg * 1000) / fishCount
    : feedingLots.length === 1 ? feedingLots[0].averageWeightG : null

  let recommended: number | null = null
  let sfrBiomass = 0
  if (unit.temperatureC !== null) {
    for (const lot of feedingLots) {
      if (!(lot.biomassKg && lot.biomassKg > 0) || !(lot.averageWeightG && lot.averageWeightG > 0)) continue
      const sfr = options.sfrFor(lot.id, lot.averageWeightG, unit.temperatureC)
      if (sfr === null || !Number.isFinite(sfr)) continue
      recommended = (recommended ?? 0) + (lot.biomassKg * sfr) / 100
      sfrBiomass += lot.biomassKg
    }
  }
  const sfrPercent = recommended !== null && sfrBiomass > 0 ? (recommended / sfrBiomass) * 100 : null

  const densityKgM3 = biomassKg === null ? null : stockingDensityKgM3(biomassKg, unitVolumeM3(unit.capacity, unit.capacityUnit))
  // One lot per unit is the farm rule (warn, never block). With several lots on
  // different tables, the strictest applies: the lowest kg/m³ limit and the
  // highest minimum flow (a set density is never exceeded, AQ 20.02.13).
  const lotTables = options.capacityForLot
    ? feedingLots.map((lot) => options.capacityForLot!(lot.id)).filter((table): table is CarryingCapacityTable => table !== null)
    : []
  const strictest = <T extends number>(pick: (table: CarryingCapacityTable) => T | null, better: (a: T, b: T) => boolean) => {
    let best: { value: T; table: CarryingCapacityTable } | null = null
    for (const table of lotTables) {
      const value = pick(table)
      if (value !== null && (!best || better(value, best.value))) best = { value, table }
    }
    return best
  }
  const densityPick = strictest((table) => capacityDensityLimit(table, unit.type, averageWeightG), (a, b) => a < b)
  const flowPick = strictest((table) => capacityMinFlowPerKg(table, averageWeightG, unit.temperatureC), (a, b) => a > b)
  const capacityTable = densityPick?.table ?? flowPick?.table ?? lotTables[0] ?? null

  return {
    unitId: unit.id,
    unitName: unit.name,
    lots: unit.lots.map((lot) => ({ id: lot.id, lotKey: lot.lotKey, preFeeding: lot.preFeeding, lineageRootId: lot.lineageRootId ?? null })),
    fishCount,
    eggCount: sumOrNull(unit.lots.filter((lot) => lot.preFeeding).map((lot) => lot.fishCount)),
    averageWeightG,
    biomassKg,
    temperatureC: unit.temperatureC,
    weightDate: feedingLots
      .map((lot) => (lot.asOfDate ? lot.asOfDate.slice(0, 10) : null))
      .filter((day): day is string => Boolean(day))
      .sort()[0] ?? null,
    temperatureDate: unit.temperatureDate ? unit.temperatureDate.slice(0, 10) : null,
    sfrPercent,
    recommendedKgPerDay: recommended,
    planKgPerDay: unit.planKgPerDay,
    planFeedingsPerDay: unit.planFeedingsPerDay ?? null,
    fedYesterdayKg: unit.fedYesterdayKg,
    fedTodayKg: unit.fedTodayKg ?? null,
    densityKgM3,
    densityLimitKgM3: capacityTable
      ? densityPick?.value ?? null
      : densityLimitFor(options.densityLimits ?? [], unit.type, averageWeightG),
    densityTableName: capacityTable?.name ?? null,
    flowLpmPerKg: biomassKg === null ? null : flowPerKg(unit.flowLpm, biomassKg),
    minFlowLpmPerKg: flowPick?.value ?? null
  }
})

/** Lots spread over more than one unit: the per-lot "Usar recomendado como plan" applies to all of them. */
export const lotsInSeveralUnits = (rows: FarmFeedRow[]): Array<{ lotId: string; lotKey: string; unitIds: string[] }> => {
  const byLot = new Map<string, { lotKey: string; unitIds: string[] }>()
  for (const row of rows) {
    for (const lot of row.lots) {
      if (lot.preFeeding) continue
      // Findings #160: one lot per unit sharing a lineage root is one lot for the office.
      const groupId = lot.lineageRootId || lot.id
      const entry = byLot.get(groupId) ?? { lotKey: lot.lotKey, unitIds: [] }
      if (!entry.unitIds.includes(row.unitId)) entry.unitIds.push(row.unitId)
      byLot.set(groupId, entry)
    }
  }
  return Array.from(byLot.entries())
    .filter(([, entry]) => entry.unitIds.length > 1)
    .map(([lotId, entry]) => ({ lotId, ...entry }))
    .sort((a, b) => a.lotKey.localeCompare(b.lotKey))
}

/** Older than this many days is shown in orange (biometry every 10–15 days; water read daily). */
export const STALE_WEIGHT_DAYS = 15
export const STALE_TEMPERATURE_DAYS = 2

/** Whole days from `day` to `today` (both YYYY-MM-DD); null without a day. */
export const daysOld = (day: string | null, today: string): number | null => {
  if (!day) return null
  const diff = Date.parse(`${today}T00:00:00Z`) - Date.parse(`${day.slice(0, 10)}T00:00:00Z`)
  return Number.isFinite(diff) ? Math.round(diff / 86_400_000) : null
}

/** kg/day to store in the plan: one decimal, as the plan editor shows it. */
export const roundPlanKg = (kg: number): number => Math.round(kg * 10) / 10

export const totalsOf = (rows: FarmFeedRow[]) => ({
  biomassKg: sumOrNull(rows.map((row) => row.biomassKg)),
  recommendedKgPerDay: sumOrNull(rows.map((row) => row.recommendedKgPerDay)),
  planKgPerDay: sumOrNull(rows.map((row) => row.planKgPerDay)),
  fedYesterdayKg: sumOrNull(rows.map((row) => row.fedYesterdayKg)),
  fedTodayKg: sumOrNull(rows.map((row) => row.fedTodayKg))
})
